import { Badge, BadgeProps, Tooltip } from "@chakra-ui/react";
import { GenericTableColumnBadge } from "gen/model/genericTableColumnBadge";
import { GenericTableIconBadge } from "gen/model/genericTableIconBadge";
import { GenericTableTextBadge } from "gen/model/genericTableTextBadge";
import { GenericTableTextBadgeMetadata } from "gen/model/genericTableTextBadgeMetadata";
import React from "react";
import {
  ClickableElementGenericLinkInfo,
  ClickableElementLinkInfo,
} from "shared/components/clickable/ClickableElementLinkInfo";
import { useClickableElementOnClickHandler } from "shared/components/clickable/ClickableElementOnClickHandler";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";

export interface DataTableCellBadgeProps {
  badge: NonNullable<GenericTableColumnBadge>;
  linkInfo?: ClickableElementLinkInfo;
}

const getLinkInfoForBadge = (
  metadata: GenericTableTextBadgeMetadata | undefined,
): ClickableElementGenericLinkInfo | undefined => {
  if (!metadata) {
    return undefined;
  }

  switch (metadata.type) {
    case "investor_transfer":
      return {
        type: "generic",
        to: rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId: metadata.fund_family_id })
          .investorTransfers({})
          .view({ investorTransferId: metadata.investor_transfer_id }).$,
      };
    default:
      return undefined;
  }
};

export const DataTableCellBadge: React.FC<DataTableCellBadgeProps> = ({
  badge,
  linkInfo,
}) => {
  switch (badge.type) {
    case "text":
      return <DataTableCellBadgeText badge={badge} linkInfo={linkInfo} />;
    case "icon":
      return <DataTableCellBadgeIcon badge={badge} />;
    default:
      return null;
  }
};

const DataTableCellBadgeIcon: React.FC<{
  badge: GenericTableIconBadge;
}> = ({ badge: { tooltip } }) => (
  <Tooltip label={tooltip}>
    <InfoCircleIcon width="14px" height="14px" color="gray.400" />
  </Tooltip>
);

const DataTableCellBadgeText: React.FC<{
  badge: GenericTableTextBadge;
  linkInfo?: ClickableElementLinkInfo;
}> = ({ badge: { color: type, text, tooltip, metadata }, linkInfo }) => {
  const linkOnClick = useClickableElementOnClickHandler();

  const badgeLinkInfo = linkInfo || getLinkInfoForBadge(metadata);
  const badgeProps: BadgeProps = {
    colorScheme: type,
    ...(badgeLinkInfo
      ? {
          variant: "button",
          onClick: (event) => linkOnClick(badgeLinkInfo, event),
          onMouseDownCapture: (e) => e.stopPropagation(),
        }
      : undefined),
  };

  return tooltip ? (
    <Tooltip label={tooltip}>
      <Badge {...badgeProps}>
        {text}
        <InfoCircleIcon />
      </Badge>
    </Tooltip>
  ) : (
    <Badge {...badgeProps}>{text}</Badge>
  );
};
